import { Dialog } from 'primereact/dialog'
import React, { useState,useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import TooltipOverlay from '../../global/TooltipOverlay'
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
import PrimeTable from '../../PrimeTable/PrimeTable'
import currencies from "../../../services/currencies.json"
import { useAppContext, usePersistedState } from '../../../AppContext'
import mainAxios from '../../../services/mainAxios';
import Loader from '../../Layout/Loader';
import AddEditOpenBankingAccount from './AddEditOpenBankingAccount';
import { useOpenBanking } from '../UseOpenBanking';
import { Column } from 'primereact/column';
import ExportCsvPrime from '../../global/ExportCsvPrime';


const OpenBankingAccounts = () => {
  const [addBankAccountVisible, setAddBankAccountVisible] = useState({show:false,id:null})
  const { t, i18n } = useTranslation("translations");
  const { loading:pageLoading,bankAccounts,getBankAccounts} = useOpenBanking()
  const [expandedData, setExpandedData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [loadingExpandData, setLoadingExpandData] = useState(false);
  const [bankAccountsForCsv, setBankAccountsForCsv] = useState([]);

  const statusOptions = useMemo(() => [
    { label: t("bankAccount.enabled"), value: "enabled" },  
    { label: t("bankAccount.deleted"), value: "deleted" },
    { label: t("bankAccount.blocked"), value: "blocked" },
  ], [t]);


  const cashAccountTypes = useMemo(() => [
    { label: "CHECKING", value: "CHECKING" },
    { label: "SAVINGS", value: "SAVINGS" },
    { label: "CREDIT", value: "CREDIT" },
  ], [])



const bankAccountColumns = useMemo(() => [
  {
    header: t(["home.actions"]),
    field: "actions",
    default: true,
    persisted: true,
    style: { minWidth: "200px", maxWidth: "220px" },
    mobile: { priority: "action", group: "actions" },
    body: (bankAccount) => (
      <div className='d-flex align-items-center'>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setAddBankAccountVisible({ show: true, id: bankAccount.id});
          }}
          className="nav-link p-0 mr-3"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={t("common.edit")}
        >
          <FontAwesomeIcon icon={faEdit} className="mr-1" />
        </Link>
        <Link to={`/bank-journal/${bankAccount.accountNo}`} className='d-flex align-items-center gap-1 cursor-pointer'>
            <FontAwesomeIcon icon={faUpRightFromSquare} size={14} className="ml-1 mt-1" />
            Transactions
        </Link>
      </div>
    ),
  },
  {
    header: "Financial Institution",
    filter: true,
    default: true,
    field: "financialInstitution.name",
    style: { minWidth: "220px", maxWidth: "220px" },
    sortable: true,
    mobile: { priority: 1, isTitle: true, group: "identity" },
    body: (bankAccount) => bankAccount.financialInstitution?.name || "",
  },
  {
    header: t(["bankAccount.accountName"]),
    filter: true,
    default: true,
    field: "branch",
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    mobile: { priority: 1, group: "identity" },
  },
  {
    header: t(["bankAccount.accountNo"]),
    filter: true,
    default: true,
    field: "accountNo",
    style: { minWidth: "240px", maxWidth: "240px" },
    sortable: true,
    mobile: { priority: 1, group: "reference" },
  },
  {
    header: "IBAN",
    field: "iban",
    filter: true,
    default: true,
    style: { minWidth: "250px", maxWidth: "250px" },
    sortable: true,
    mobile: { priority: 1, group: "reference" },
  },
  {
    header: "BBAN",
    field: "bban",
    filter: true,
    default: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    mobile: { priority: 1, group: "reference" },
  },
  {
    header: "PAN",
    field: "pan",
    filter: true,
    default: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    mobile: { priority: 1, group: "reference" },
  },
  {
    header: "Masked PAN",
    field: "maskedPan",
    filter: true,
    default: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    mobile: { priority: 1, group: "reference" },
  },
  {
    header: t(["invoice.currency"]),
    field: "currency",
    filter: true,
    default: true,
    sortable: true,
    style: { minWidth: "180px", maxWidth: "200px" },
    filterType: "DROPDOWN",
    filterConfig: {
      filter: true,
      options: currencies,
    },
    mobile: { priority: 1, group: "financial" },
  },
  {
    header: "Cash Account Type",
    field: "cashAccountType",
    filter: true,
    default: true,
    filterType: "DROPDOWN",
    filterConfig: {
      filter: true,
      options: cashAccountTypes,
    },
    sortable: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    body: (bankAccount) => bankAccount.cashAccountType || "Current",
    mobile: { priority: 1, group: "status" },
  },
  {
    header: "Open Banking",
    field: "isOpenBanking",
    filter: true,
    default: true,
    filterType: "TRISTATECHECKBOX",
    dataType: "boolean",
    sortable: true,
    style: { minWidth: "150px", maxWidth: "150px" },
    mobile: { priority: 1, isBadge: true, group: "status" },
  },
  {
    header: t("company.active"),
    field: "active",
    filter: true,
    default: true,
    filterType: "TRISTATECHECKBOX",
    dataType: "boolean",
    sortable: true,
    style: { minWidth: "150px", maxWidth: "150px" },
    mobile: { priority: 1, isBadge: true, group: "status" },
  }
], [t, currencies, cashAccountTypes]);

const [selectedColumns, setSelectedColumns] = usePersistedState(
  bankAccountColumns.map((col) => ({ field: col.field, show: !!col.default, ...(col?.persisted ? { persisted: true } : {}) })),
  "openBankingAccountsColumns_v4"
);


const expandedBankAccountColumns=[
  {
    header: "Resource Id",
    field: "resourceId",
    filter: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    body: (bankAccount) => bankAccount.msisdn || "",
  },
  {
    header: "MSISDN",
    field: "msisdn",
    filter: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    body: (bankAccount) => bankAccount.msisdn || "",
  },
  {
    header: "Owner Name",
    field: "ownerName",
    filter: true,
    style: { minWidth: "220px", maxWidth: "220px" },
    sortable: true,
    body: (bankAccount) => bankAccount.ownerName || "",
  },
  {
    header: "Psu Name",
    field: "psuName",
    filter: true,
    style: { minWidth: "220px", maxWidth: "220px" },
    sortable: true,
  },
  {
    header: "Display Name",
    field: "displayName",
    filter: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
  },
  {
    header: "Status",
    field: "status",
    filter: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
    body: (bankAccount) => bankAccount.displayName || "",
  },
  {
    header: "Linked Accounts",
    field: "linkedAccounts",
    filter: true,
    style: { minWidth: "200px", maxWidth: "200px" },
    sortable: true,
  },
  {
    header: "Usage",
    field: "usage",
    filter: true,
    sortable: true,
    style: { minWidth: "100px", maxWidth: "100px" },
    filterType: "DROPDOWN",
    filterConfig: {
      filter: true,
      options: ["PRIV", "ORG"],
    },
    body: (bankAccount) => bankAccount.usage || "",
  },
  {
    header: "Details",
    field: "details",
    filter: true,
    style: { minWidth: "250px", maxWidth: "250px" },
    sortable: true,
    body: (bankAccount) => bankAccount.details || "",
  }
]


  const onRowExpand = (event) => {
    setLoadingExpandData(true);
    setExpandedRows([event.data]);
    mainAxios
      .get(`/api/accounts/details/${event?.data?.id}`)
      .then((res) => {
        if (res.status==200) {
          setExpandedData(res.data);
          setLoadingExpandData(false);
        }
      });
  };
  
  const onRowCollapse = (event) => {
    setExpandedRows([]);
  };  



  const rowExpansionTemplate = (data) => {
        if (loadingExpandData) return <Loader size="small" />
    return (
      <div className="d-flex flex-column ml-3 my-3">
        <div className="d-flex align-items-center">
          <h4 className="my-1">Bank Account Details</h4>
        </div>
          <PrimeTable
            value={expandedData ?? []}
            columns={expandedBankAccountColumns}
            showFilters={false}
            filterDisplay={false}
            responsiveLayout="scroll"
            showHeader={false}
            simpleTable={true}
            paginator={false}
            style={{maxWidth:"100vw"}}
          >
            <Column style={{width:40}}/>
          </PrimeTable>
      </div>
    )
  }

    if(pageLoading && !bankAccounts) {
      return <div className='h-screen w-screen'>
        <Loader/>
      </div>
    }

    // Export icon for headerIcons
    const exportIcon = (
      <ExportCsvPrime
        selectedColumns={selectedColumns}
        iconOnly
        tooltip="Export CSV"
        data={bankAccounts || []}
        columns={bankAccountColumns}
        onClick={() => Promise.resolve()}
        excludedColumns={["actions"]}
      />
    );

    return (
        <>
        <div className="d-flex flex-wrap justify-content-between align-items-center my-2">
          <h3 className="mb-0">{t("company.bankAccounts")}</h3>
          <div className="d-flex justify-content-end align-items-center">
            <button className="btn btn-sm btn-outline-primary justify-content-center align-items-center mb-0"
              onClick={() => setAddBankAccountVisible({ show: true, id: null })}>
              <FontAwesomeIcon icon={faEdit} className="mr-1" />
              {t("bankAccount.add")}
            </button>
          </div>
        </div>
            <Dialog
                header={addBankAccountVisible.id ? t("bankAccount.edit") : t("bankAccount.add")}
                visible={addBankAccountVisible.show}
                onHide={() => setAddBankAccountVisible((state) => ({ ...state, show: false }))}
                style={{ width: "30vw",maxHeight:"99vh" }}
                breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                closable={true}

            >
                <AddEditOpenBankingAccount
                    id={addBankAccountVisible.id}
                    onSuccess={() => {
                        getBankAccounts()
                        setAddBankAccountVisible({ show: false, id: null })
                    }}
                />
            </Dialog>
        <PrimeTable
          stripedRows={false}
          pageLoading={pageLoading}
          value={bankAccounts?? []}
          columns={bankAccountColumns}
          gridKey="openBankingAccountsColumns_v4"
          selectedColumns={[selectedColumns, setSelectedColumns]}
          showFilters={true}
          filterDisplay={"row"}
          responsiveLayout="scroll"
          dataKey="id"
          scrollHeight="580px"
          simpleTable={true}
          reorderableColumns={true}
          headerIcons={exportIcon}
          expandedRows={expandedRows}
          onRowExpand={onRowExpand}
          onRowCollapse={onRowCollapse}
          rowExpansionTemplate={rowExpansionTemplate}
        >
      
          <Column
            expander={() => true}
            style={{ width: "0.5em" }}
            header=""
            field="expansion"
            frozen={true}
          />
        </PrimeTable>
        </>
    )
}

export default OpenBankingAccounts